import { ArrowLeft, ArrowRight, ListOrdered, Package, QrCode, Boxes, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { cn } from '@/lib/utils';

interface Step {
  icon: LucideIcon;
  tag: string;
  title: string;
  body: string;
}

const STEPS: Step[] = [
  {
    icon: Package,
    tag: 'Models tab',
    title: 'Add your 3D models',
    body:
      'Drag and drop .glb files into the Models tab, or use whatever is already there. These are the 3D objects people will see in AR.',
  },
  {
    icon: ListOrdered,
    tag: 'Sequences tab',
    title: 'Arrange them into a sequence',
    body:
      'A sequence is just an ordered list of models — like a slideshow. Build one from your models, or reuse one that already exists.',
  },
  {
    icon: Boxes,
    tag: 'Rooms tab',
    title: 'Start a room to go live',
    body:
      'Create a room and pick a sequence. Everyone who joins sees the exact same model, and you step through the sequence live for the whole room at once.',
  },
  {
    icon: QrCode,
    tag: 'Rooms tab',
    title: 'Bring people in and stay in control',
    body:
      'Project the QR code (or open the full-screen Audience view) so phones can scan and join. Pick who is allowed in — everyone, just your team, or just admins — from the room’s settings.',
  },
];

const STORAGE_KEY = 'casper.onboarding.seen';

export function markOnboardingSeen() {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    /* ignore — worst case the guide reappears next visit */
  }
}

export function hasSeenOnboarding(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

interface OnboardingGuideProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function OnboardingGuide({ open, onOpenChange }: OnboardingGuideProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const step = STEPS[stepIndex];
  const Icon = step.icon;
  const isLast = stepIndex === STEPS.length - 1;

  const close = () => {
    markOnboardingSeen();
    onOpenChange(false);
    // Reset for next open so a returning visitor starts from step 1 again.
    setStepIndex(0);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent className="max-w-lg" onInteractOutside={(e) => e.preventDefault()}>
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
            How cAspeR works &middot; {stepIndex + 1} of {STEPS.length}
          </span>
          <button
            type="button"
            onClick={close}
            className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            Skip
          </button>
        </div>

        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <div className="grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary">
            <Icon className="h-7 w-7" />
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">{step.tag}</p>
            <h2 className="text-xl font-semibold tracking-tight">{step.title}</h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">{step.body}</p>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={cn(
                  'h-1.5 w-6 rounded-full transition-colors',
                  i === stepIndex ? 'bg-primary' : 'bg-muted',
                )}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {stepIndex > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setStepIndex((i) => i - 1)}>
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
            )}
            {isLast ? (
              <Button size="sm" onClick={close}>
                Let&rsquo;s go
              </Button>
            ) : (
              <Button size="sm" onClick={() => setStepIndex((i) => i + 1)}>
                Next <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
